import { WebSocketMessage } from '../types/bulkIndexing';
import { logger } from '../utils/logger';
import { validateWebSocketMessage } from '../types/websocketValidation';
import { appendTokenParam, getToken } from '../auth/keycloak';

/**
 * Bulk Indexing WebSocket Client
 * 
 * Provides real-time updates for bulk indexing jobs via WebSocket.
 * Features:
 * - Auto-reconnection with exponential backoff
 * - Subscription management
 * - Type-safe message handling
 * - Event emission for React components
 */
export class BulkIndexingWebSocket {
  private static instance: BulkIndexingWebSocket | null = null;
  private socket: WebSocket | null = null;
  private jobId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000; // Initial delay in ms
  private maxReconnectDelay = 30000; // Max delay in ms
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isIntentionalClose = false;
  private messageHandlers: Set<(message: WebSocketMessage) => void> = new Set();
  private connectionHandlers: Set<(connected: boolean) => void> = new Set();
  private errorHandlers: Set<(error: string) => void> = new Set();
  private isConnected = false;
  private pingInterval: NodeJS.Timeout | null = null;
  private pingIntervalMs = 60000; // 60 seconds (optimized for production - reduced network traffic)

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): BulkIndexingWebSocket {
    if (!BulkIndexingWebSocket.instance) {
      BulkIndexingWebSocket.instance = new BulkIndexingWebSocket();
    }
    return BulkIndexingWebSocket.instance;
  }

  /**
   * Get WebSocket URL for a job
   */
  private getWebSocketUrl(jobId: string): string {
    // Use environment variable if set
    if (process.env.REACT_APP_WS_URL) {
      // If REACT_APP_WS_URL is set, assume it includes the full path or base URL
      const baseUrl = process.env.REACT_APP_WS_URL;
      // Check if it already includes the path
      if (baseUrl.includes('/bulk-indexing') || baseUrl.includes('/api/v1')) {
        const url = `${baseUrl}/${jobId}`.replace(/\/+/g, '/').replace(':/', '://');
        logger.debug('Using environment WS_URL (with path)', { url });
        return url;
      }
      // Otherwise, construct full path
      const url = `${baseUrl}/api/v1/ws/bulk-indexing/ws/${jobId}`.replace(/\/+/g, '/').replace(':/', '://');
      logger.debug('Using environment WS_URL (constructed path)', { url });
      return url;
    }
    
    // Construct URL based on current location
    // CRITICAL: Use /api/v1 prefix since foundation app is mounted at /api/v1
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : '';
    // Foundation app is mounted at /api/v1, so WebSocket path must include this prefix
    const url = `${protocol}//${host}${port}/api/v1/ws/bulk-indexing/ws/${jobId}`;
    
    logger.debug('Constructed WebSocket URL', { url });
    return url;
  }

  /**
   * Connect to WebSocket for a specific job
   */
  public async connect(jobId: string): Promise<void> {
    // If already connected to this job, do nothing
    if (this.isConnected && this.jobId === jobId) {
      logger.debug('Already connected to job', { jobId });
      return;
    }

    // Disconnect from previous job if needed
    if (this.socket && this.jobId !== jobId) {
      logger.debug('Disconnecting from previous job', { previousJobId: this.jobId, newJobId: jobId });
      await this.disconnect();
    }

    this.jobId = jobId;
    this.isIntentionalClose = false;

    // Keycloak access token for backend WebSocket authentication.
    const authToken = await getToken();

    return new Promise((resolve, reject) => {
      try {
        const wsUrl = appendTokenParam(this.getWebSocketUrl(jobId), authToken);
        logger.debug('Connecting to WebSocket', { url: wsUrl, jobId });

        this.socket = new WebSocket(wsUrl);

        this.socket.onopen = () => {
          logger.info('WebSocket connection established', { jobId });
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.notifyConnectionChange(true);
          this.startPingInterval();
          resolve();
        };

        this.socket.onmessage = (event) => {
          this.handleMessage(event);
        };

        this.socket.onclose = (event) => {
          const isRecoverable = this.isRecoverableCloseCode(event.code);
          
          logger.warn(
            `WebSocket connection closed`,
            {
              code: event.code,
              reason: event.reason,
              recoverable: isRecoverable,
              jobId: this.jobId,
            }
          );

          this.isConnected = false;
          this.notifyConnectionChange(false);
          this.stopPingInterval();

          // Attempt reconnection only for recoverable errors and if not intentional close
          if (!this.isIntentionalClose && isRecoverable) {
            const errorMessage = this.getErrorMessage(event.code, event.reason);
            this.notifyError(errorMessage);
            this.scheduleReconnect();
          } else if (!isRecoverable) {
            // Non-recoverable error - notify user
            const errorMessage = this.getErrorMessage(event.code, event.reason);
            this.notifyError(`Connection error: ${errorMessage}. Please refresh the page.`);
            logger.error('Non-recoverable WebSocket error', new Error(errorMessage), {
              code: event.code,
              reason: event.reason,
              jobId: this.jobId,
            });
          }
        };

        this.socket.onerror = (error) => {
          const errorMessage = 'Failed to establish WebSocket connection. Retrying...';
          logger.error('WebSocket connection error', error, {
            jobId: this.jobId,
            recoverable: true,
          });
          this.notifyError(errorMessage);
          reject(error);
        };
      } catch (error) {
        logger.error('Error setting up WebSocket', error, {
          jobId: this.jobId,
        });
        this.notifyError('Failed to initialize WebSocket connection');
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket
   */
  public async disconnect(): Promise<void> {
    logger.debug('Disconnecting WebSocket', { jobId: this.jobId });
    this.isIntentionalClose = true;
    this.stopPingInterval();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      const socket = this.socket;
      this.socket = null;

      return new Promise<void>((resolve) => {
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          socket.onclose = () => {
            logger.debug('WebSocket disconnected successfully');
            this.isConnected = false;
            this.notifyConnectionChange(false);
            this.jobId = null;
            resolve();
          };
          socket.close(1000, 'Normal closure');
        } else {
          logger.debug('WebSocket socket already closed');
          this.isConnected = false;
          this.notifyConnectionChange(false);
          this.jobId = null;
          resolve();
        }
      });
    }

    this.isConnected = false;
    this.jobId = null;
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('Max reconnection attempts reached', undefined, {
        jobId: this.jobId,
        attempts: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts,
      });
      this.notifyError('Failed to reconnect after multiple attempts');
      return;
    }

    this.reconnectAttempts++;

    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    logger.debug('Scheduling WebSocket reconnection', {
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      delayMs: delay,
      jobId: this.jobId,
    });

    this.reconnectTimer = setTimeout(async () => {
      if (this.jobId && !this.isIntentionalClose) {
        logger.debug('Attempting WebSocket reconnection', { jobId: this.jobId });
        try {
          await this.connect(this.jobId);
        } catch (error) {
          logger.error('WebSocket reconnection failed', error, { jobId: this.jobId });
          // scheduleReconnect will be called by onclose handler
        }
      }
    }, delay);
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.isConnected && this.socket?.readyState === WebSocket.OPEN) {
        logger.debug('Sending WebSocket ping');
        this.socket.send('ping');
      }
    }, this.pingIntervalMs);
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(event: MessageEvent): void {
    try {
      // Handle ping/pong messages
      if (event.data === 'pong') {
        logger.debug('Received WebSocket pong');
        return;
      }

      // Validate and parse WebSocket message with runtime type checking
      let message: WebSocketMessage;
      try {
        message = validateWebSocketMessage(event.data);
        logger.debug('Received WebSocket message', { messageType: message.message_type, jobId: message.job_id });
      } catch (validationError) {
        logger.error('Invalid WebSocket message received', validationError as Error, {
          rawData: event.data,
        });
        this.notifyError(`Invalid message format: ${validationError instanceof Error ? validationError.message : 'Unknown error'}`);
        return;
      }

      // Notify all message handlers
      this.messageHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          logger.error('Error in WebSocket message handler', error);
        }
      });

      // Emit custom events for specific message types
      this.emitCustomEvent(message);
    } catch (error) {
      logger.error('Error handling WebSocket message', error);
    }
  }

  /**
   * Emit custom event for React components
   */
  private emitCustomEvent(message: WebSocketMessage): void {
    const eventName = `bulk_indexing_${message.message_type}`;
    window.dispatchEvent(
      new CustomEvent(eventName, {
        detail: message,
      })
    );
  }

  /**
   * Register message handler
   */
  public onMessage(handler: (message: WebSocketMessage) => void): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /**
   * Register connection status handler
   */
  public onConnectionChange(handler: (connected: boolean) => void): () => void {
    this.connectionHandlers.add(handler);
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }

  /**
   * Register error handler
   */
  public onError(handler: (error: string) => void): () => void {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  /**
   * Notify connection change
   */
  private notifyConnectionChange(connected: boolean): void {
    this.connectionHandlers.forEach((handler) => {
      try {
        handler(connected);
      } catch (error) {
        logger.error('Error in WebSocket connection handler', error);
      }
    });
  }

  /**
   * Notify error
   */
  private notifyError(error: string): void {
    this.errorHandlers.forEach((handler) => {
      try {
        handler(error);
      } catch (error) {
        logger.error('Error in WebSocket error handler', error);
      }
    });
  }

  /**
   * Get connection status
   */
  public getIsConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Get current job ID
   */
  public getCurrentJobId(): string | null {
    return this.jobId;
  }

  /**
   * Determine if a WebSocket close code is recoverable
   * 
   * Recoverable codes (should retry):
   * - 1006: Abnormal closure (network issues, connection lost)
   * - 1012: Service restart
   * - 1013: Try again later
   * 
   * Non-recoverable codes (should not retry):
   * - 1000: Normal closure
   * - 1001: Going away (server shutdown)
   * - 1002: Protocol error
   * - 1003: Unsupported data
   * - 1007: Invalid frame payload
   * - 1008: Policy violation
   * - 1009: Message too big
   * - 1010: Mandatory extension
   * - 1011: Internal server error (may be recoverable)
   */
  private isRecoverableCloseCode(code: number): boolean {
    const recoverableCodes = [1006, 1012, 1013];
    const nonRecoverableCodes = [1000, 1001, 1002, 1003, 1007, 1008, 1009, 1010];
    
    if (recoverableCodes.includes(code)) {
      return true;
    }
    
    if (nonRecoverableCodes.includes(code)) {
      return false;
    }
    
    // For unknown codes, default to recoverable (safer)
    return true;
  }

  /**
   * Get user-friendly error message from close code
   */
  private getErrorMessage(code: number, reason?: string): string {
    const errorMessages: Record<number, string> = {
      1000: 'Connection closed normally',
      1001: 'Server is going away',
      1002: 'Protocol error',
      1003: 'Unsupported data format',
      1006: 'Connection lost. Attempting to reconnect...',
      1007: 'Invalid data received',
      1008: 'Policy violation',
      1009: 'Message too large',
      1010: 'Extension negotiation failed',
      1011: 'Server error',
      1012: 'Service restarting. Reconnecting...',
      1013: 'Service temporarily unavailable. Reconnecting...',
    };

    return errorMessages[code] || reason || `Connection closed (code: ${code})`;
  }
}

// Export singleton instance
export const bulkIndexingWebSocket = BulkIndexingWebSocket.getInstance();



